import { z } from 'zod';
import { ACTION_STATUSES } from './actions';
import { ALERT_CRITICALITIES, ALERT_STATUSES, ALERT_TYPES } from './alerts';
import { KPI_UNIT_ACCESS, monthPointSchema } from './results';
import { TRAFFIC_LIGHTS } from './status';

/**
 * Drill-down (T-144, RF-37, PRD §11.24; DEC-058): de toda visão consolidada até a causa operacional — empresa →
 * perspectiva → objetivo → KPI → unidade → resultados e justificativas, desvios e ações.
 */

/** Itens de cada lista do nível da unidade (os mais recentes e os abertos primeiro). */
export const DRILL_LIST_LIMIT = 20;

const uuid = (message: string) => z.string().uuid(message);
const period = {
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
};
const samePeriod = <T extends { year?: number; month?: number }>(v: T) =>
  (v.year === undefined) === (v.month === undefined);
const periodMessage = { message: 'Informe o ano e o mês juntos.', path: ['month'] };

/**
 * Trilha do caminho aberto: cada nível com o nome e o número do mês. Perspectiva e objetivo podem vir do nível de
 * baixo (o KPI traz a sua perspectiva; o objetivo, a dele); o objetivo do KPI só entra quando vem no pedido.
 */
export const drillTrailQuerySchema = z
  .object({
    perspectiveId: uuid('Perspectiva inválida.').optional(),
    objectiveId: uuid('Objetivo inválido.').optional(),
    kpiId: uuid('Indicador inválido.').optional(),
    businessUnitId: uuid('Unidade inválida.').optional(),
    ...period,
  })
  .refine(samePeriod, periodMessage)
  .refine((v) => !v.businessUnitId || v.kpiId, {
    message: 'A unidade é um nível abaixo do indicador: informe o indicador.',
    path: ['businessUnitId'],
  });
export type DrillTrailQuery = z.input<typeof drillTrailQuerySchema>;

const light = z.enum(TRAFFIC_LIGHTS);
const level = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Performance do mês (null = sem dado). */
  performance: z.number().nullable(),
  light,
});
const ref = z.object({ id: z.string().uuid(), name: z.string() });

export const drillTrailSchema = z.object({
  project: ref,
  year: z.number().int(),
  month: z.number().int(),
  /** O topo: a performance da empresa no mês (id = o projeto). */
  company: level,
  perspective: level.nullable(),
  objective: level.nullable(),
  kpi: level.extend({ code: z.string() }).nullable(),
  unit: level.nullable(),
});
export type DrillTrail = z.infer<typeof drillTrailSchema>;

/** O KPI numa unidade no mês: a causa operacional (resultados e justificativas, desvios e ações). */
export const drillUnitQuerySchema = z
  .object({
    kpiId: uuid('Indicador inválido.'),
    businessUnitId: uuid('Unidade inválida.'),
    ...period,
  })
  .refine(samePeriod, periodMessage);
export type DrillUnitQuery = z.input<typeof drillUnitQuerySchema>;

const list = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ total: z.number().int(), items: z.array(item) }).nullable();

export const drillUnitSchema = z.object({
  project: ref,
  year: z.number().int(),
  month: z.number().int(),
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    direction: z.string(),
    perspective: ref,
    /** Objetivos ativos a que o KPI está ligado (para subir pela trilha). */
    objectives: z.array(ref),
  }),
  unit: z.object({ id: z.string().uuid(), name: z.string(), archived: z.boolean() }),
  /** AREA: vê o KPI pela área; UNIT: só pela unidade (DEC-037). */
  access: z.enum(KPI_UNIT_ACCESS),
  /** Meta, realizado e performance da unidade no mês e no acumulado. */
  current: monthPointSchema,
  /** Os 12 meses do ano na unidade. */
  months: z.array(monthPointSchema),
  /** Resultados lançados na unidade no ano até o mês (versão vigente), com justificativa e evidência. */
  results: z.array(
    z.object({
      id: z.string().uuid(),
      periodStart: z.string(),
      periodEnd: z.string(),
      referenceDate: z.string(),
      value: z.number().nullable(),
      justification: z.string().nullable(),
      evidenceName: z.string().nullable(),
      version: z.number().int(),
      /** Motivo da última correção (da versão 2 em diante). */
      reason: z.string().nullable(),
      createdByName: z.string(),
      createdAt: z.string(),
    }),
  ),
  /** Desvios: alertas do KPI no ano até o mês, da unidade ou do KPI todo (null = sem o módulo de alertas). */
  deviations: list(
    z.object({
      id: z.string().uuid(),
      type: z.enum(ALERT_TYPES),
      criticality: z.enum(ALERT_CRITICALITIES),
      status: z.enum(ALERT_STATUSES),
      title: z.string(),
      message: z.string(),
      periodStart: z.string(),
      periodEnd: z.string(),
      /** Da unidade (true) ou do KPI todo (false). */
      atUnit: z.boolean(),
      resolution: z.string().nullable(),
      responsible: z.string().nullable(),
    }),
  ),
  /** Ações dos planos ligados a esses desvios ou às iniciativas dos objetivos do KPI na unidade (null = sem o módulo). */
  actions: list(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      status: z.enum(ACTION_STATUSES),
      dueDate: z.string().nullable(),
      late: z.boolean(),
      responsible: z.string().nullable(),
      plan: ref,
    }),
  ),
});
export type DrillUnit = z.infer<typeof drillUnitSchema>;
